// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ParkingSquare, Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, SetupGuide, Skeleton, ConfirmDialog } from '@/components/ui'
import { useZones, useDeleteZone } from '@/hooks/useParkingZones'
import { useProperty } from '@/hooks/useProperties'
import type { ParkingZone } from '@/types/parking'

function getZoneTypeBadge(zoneType: ParkingZone['zone_type'], label: string) {
  switch (zoneType) {
    case 'car':
      return <Badge variant="info">{label}</Badge>
    case 'motorcycle':
      return <Badge variant="warning">{label}</Badge>
    case 'mixed':
    default:
      return <Badge variant="ghost">{label}</Badge>
  }
}

export function ParkingZoneListPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const { propertyId } = useParams<{ propertyId: string }>()
  const propertyIdNum = Number(propertyId)

  const { data: zonesData, isLoading, error, isFetching } = useZones(propertyIdNum)
  const { data: property } = useProperty(propertyIdNum)
  const deleteZone = useDeleteZone()

  const [confirmDelete, setConfirmDelete] = useState<{ id: number; name: string } | null>(null)

  const zones = zonesData?.data || []

  const handleDelete = async () => {
    if (!confirmDelete) return
    await deleteZone.mutateAsync(confirmDelete.id)
    setConfirmDelete(null)
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: property?.name ?? '...', href: `/properties/${propertyId}` },
            { label: t('zones.title') },
          ]}
        />

        <PageHeader
          title={t('zones.title')}
          isRefreshing={isFetching && !isLoading}
          actions={
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate(`/properties/${propertyId}/parking/zones/new`)}
            >
              {t('zones.add')}
            </Button>
          }
        />

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <Skeleton className="h-6 w-2/3 mb-2" />
                <Skeleton className="h-4 w-full mb-4" />
                <Skeleton className="h-4 w-1/2" />
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              action={
                <Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>
              }
            />
          </Card>
        ) : zones.length === 0 ? (
          <Card>
            <SetupGuide
              icon={<ParkingSquare className="w-16 h-16" />}
              title={t('setup_guide.zones.title')}
              description={t('setup_guide.zones.description')}
              prerequisites={[
                {
                  label: t('setup_guide.prereq_property'),
                  met: !!property,
                  link: '/properties',
                  linkLabel: t('setup_guide.prereq_setup_property'),
                },
              ]}
              prerequisitesTitle={t('setup_guide.prerequisites_title')}
              steps={[
                {
                  title: t('setup_guide.zones.steps.create_zone.title'),
                  description: t('setup_guide.zones.steps.create_zone.description'),
                },
                {
                  title: t('setup_guide.zones.steps.set_capacity.title'),
                  description: t('setup_guide.zones.steps.set_capacity.description'),
                },
                {
                  title: t('setup_guide.zones.steps.monitor.title'),
                  description: t('setup_guide.zones.steps.monitor.description'),
                },
              ]}
              action={
                <Button
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => navigate(`/properties/${propertyId}/parking/zones/new`)}
                >
                  {t('zones.add')}
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {zones.map((zone) => {
              const occupancyPercent =
                zone.total_slots > 0
                  ? Math.round((zone.occupied_slots / zone.total_slots) * 100)
                  : 0
              const progressColor =
                occupancyPercent >= 90
                  ? 'progress-error'
                  : occupancyPercent >= 70
                    ? 'progress-warning'
                    : 'progress-success'
              const hasActivePermits = zone.occupied_slots > 0

              return (
                <Card key={zone.id}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-base">{zone.name}</h3>
                      <div className="mt-1">
                        {getZoneTypeBadge(
                          zone.zone_type,
                          t(`zones.types.${zone.zone_type}`)
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-base-content/60">{t('zones.total_slots')}</span>
                      <span className="font-medium">{zone.total_slots}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-base-content/60">{t('zones.occupied_slots')}</span>
                      <span className="font-medium">{zone.occupied_slots}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-base-content/60">{t('zones.available_slots')}</span>
                      <span className="font-medium">{zone.available_slots}</span>
                    </div>
                  </div>

                  {/* Occupancy progress bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-base-content/60">{t('zones.occupancy')}</span>
                      {occupancyPercent >= 100 && (
                        <span className="text-xs text-error font-medium">{t('zones.full_warning')}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <progress
                        className={`progress ${progressColor} w-full`}
                        value={occupancyPercent}
                        max="100"
                      />
                      <span className="text-xs text-base-content/60 whitespace-nowrap">
                        {occupancyPercent}%
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-base-200 flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1"
                      leftIcon={<Pencil className="w-3 h-3" />}
                      onClick={() => navigate(`/properties/${propertyId}/parking/zones/${zone.id}/edit`)}
                    >
                      {tc('actions.edit')}
                    </Button>
                    <div
                      className="flex-1"
                      title={
                        hasActivePermits
                          ? t('zones.delete_has_permits')
                          : undefined
                      }
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        className="w-full text-error hover:bg-error/10"
                        leftIcon={<Trash2 className="w-3 h-3" />}
                        disabled={hasActivePermits}
                        onClick={() => setConfirmDelete({ id: zone.id, name: zone.name })}
                      >
                        {tc('actions.delete')}
                      </Button>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title={tc('actions.delete')}
        message={t('zones.delete_confirm', { name: confirmDelete?.name ?? '' })}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteZone.isPending}
      />
    </>
  )
}
